import { ArrowLeftRight, SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useApp } from '../app/AppContext';
import { Atmosphere } from '../components/Atmosphere';
import { PhaseLabel } from '../components/PhaseLabel';
import { RecommendationCard } from '../components/RecommendationCard';
import { recommendFragrances } from '../domain/recommendation/engine';
import { analytics } from '../services/analytics';
import { enrichRecommendationCopy } from '../services/recommendation-copy';

export function RecommendationsPage() {
  const { profile } = useApp();
  const rankedRecommendations = useMemo(
    () =>
      profile.styleProfile
        ? recommendFragrances({
            style: profile.styleProfile,
            preferences: profile.preferences,
            feedbackWeights: profile.feedbackWeights,
            testedIds: profile.testedIds,
            refined: profile.refined,
          })
        : [],
    [profile],
  );
  const [recommendations, setRecommendations] = useState(rankedRecommendations);

  useEffect(() => {
    let current = true;
    setRecommendations(rankedRecommendations);
    if (profile.styleProfile) {
      void enrichRecommendationCopy(
        rankedRecommendations,
        profile.styleProfile,
        profile.preferences,
      ).then((items) => {
        if (current) setRecommendations(items);
      });
    }
    return () => {
      current = false;
    };
  }, [rankedRecommendations, profile.preferences, profile.styleProfile]);

  useEffect(() => {
    if (recommendations.length)
      analytics.track('recommendations_viewed', { refined: profile.refined });
  }, [recommendations.length, profile.refined]);

  if (!profile.styleProfile) return <Navigate to="/discover" replace />;

  return (
    <div className="recommendations-page page-pad">
      <Atmosphere variant={profile.refined ? 'pink' : 'cyan'} />
      <header className="selection-header">
        <PhaseLabel index={profile.refined ? 'Round 02' : 'Round 01'}>
          {profile.refined ? 'Refined after testing' : 'The selection'}
        </PhaseLabel>
        <h1>
          Five ways to
          <br />
          <i>interpret you.</i>
        </h1>
        <p>
          Five distinct editorial roles, grounded in catalogue evidence and shaped by your
          controlled Style Card. These are starting directions—test every fragrance physically.
        </p>
        <div className="header-actions">
          <Link to="/compare" className="button">
            <ArrowLeftRight /> Compare five
          </Link>
          <Link to="/preferences" className="button">
            <SlidersHorizontal /> Adjust inputs
          </Link>
        </div>
      </header>
      <section className="recommendation-list" aria-label="Five fragrance recommendations">
        {recommendations.map((recommendation, index) => (
          <RecommendationCard
            key={recommendation.fragrance.fragrance_id}
            recommendation={recommendation}
            index={index}
          />
        ))}
      </section>
      <aside className="method-note">
        <strong>How selection works</strong>
        <p>
          Hard exclusions come first, followed by stated preferences, testing feedback, visual-label
          hypotheses and small contextual cues. Every fragrance signal must resolve to an actual
          catalogue field; unknown data contributes nothing.
        </p>
        <Link to="/profile">See your profile and learning history</Link>
      </aside>
    </div>
  );
}
